import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import { type IconType } from 'react-icons';
import {
  Flex,
  Text,
  Skeleton,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Checkbox,
  useColorModeValue,
  useColorMode,
  useToken,
  Alert,
  AlertIcon,
  Select,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import {
  SearchIcon,
  ExternalLinkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LinkIcon,
  CalendarIcon,
} from '@chakra-ui/icons';
import {
  MdDirectionsCar,
  MdHome,
  MdStorefront,
  MdVerifiedUser,
  MdMoreVert,
  MdLinkOff,
  MdRefresh,
  MdContentCopy,
  MdEditNote,
  MdHistory,
  MdInfoOutline,
  MdPrint,
  MdFolderOpen,
} from 'react-icons/md';
// @ts-ignore
import { DateRangePicker } from 'react-date-range';

import Card from 'components/card/Card';
import {
  LINES_OF_BUSINESS,
  LOB_PERSONAL_AUTO,
  LOB_PL_SPECIALTY,
  LOB_CL_PROPERTY,
} from 'variables/constants';
import { formatDate, formatCurrency } from '../constants';
import { parseDateString, formatDateToString } from 'utils/helpers';
import type { DateRange } from '../hooks/useContactRfqs';

import type { APIMarketplaceQuoteData } from 'types/api.types';

export interface QuoteRowData extends APIMarketplaceQuoteData {}

const formatPolicyType = (
  lob: number,
): { policyType: string; icon: IconType } => {
  if (lob === LOB_PL_SPECIALTY) {
    return { policyType: 'PL Specialty', icon: MdVerifiedUser };
  }
  if (lob === LOB_CL_PROPERTY) {
    return { policyType: 'CL Property', icon: MdStorefront };
  }

  const isAuto = lob === LOB_PERSONAL_AUTO;

  return {
    policyType: isAuto ? 'Auto' : 'Property',
    icon: isAuto ? MdDirectionsCar : MdHome,
  };
};

const isRfqOnlyLob = (lob: number): boolean =>
  lob === LOB_PL_SPECIALTY || lob === LOB_CL_PROPERTY;

const ROW_ACTIONS: { key: string; label: string; icon: IconType }[] = [
  { key: 'quote', label: 'Quote', icon: MdRefresh },
  { key: 'copy-quote', label: 'Copy & Quote', icon: MdContentCopy },
  { key: 'notes', label: 'Notes', icon: MdEditNote },
  { key: 'activity', label: 'Activity', icon: MdHistory },
  { key: 'details', label: 'Details', icon: MdInfoOutline },
  { key: 'print', label: 'Print', icon: MdPrint },
  { key: 'open-binder', label: 'Open Binder', icon: MdFolderOpen },
];

export interface QuotesTableFilters {
  lineOfBusiness: string;
}

interface QuotesTableProps {
  quotes: QuoteRowData[] | undefined;
  isFetchingRfqs?: boolean;
  error: Error | null;
  onQuoteClick: (quote: QuoteRowData) => void;
  onGroupQuoteClick?: (
    allQuotes: QuoteRowData[],
    openQuote: QuoteRowData,
  ) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  filters: QuotesTableFilters;
  onFiltersChange: (filters: QuotesTableFilters) => void;
  dateRange: DateRange;
  onDateRangeSearch: (dateRange: DateRange) => void;
  onOpenQuotes: () => void;
  onCombineQuotes?: (quoteIds: number[]) => void;
  onUncombineQuote?: (quoteId: number) => void;
  isCombining?: boolean;
  isUncombining?: boolean;
}

interface ProcessedRow {
  type: 'single' | 'combined';
  quote: QuoteRowData;
  groupQuotes?: QuoteRowData[];
}

const QuotesTable = ({
  quotes,
  isFetchingRfqs,
  error,
  onQuoteClick,
  onGroupQuoteClick,
  selectedIds,
  onSelectionChange,
  filters,
  onFiltersChange,
  dateRange,
  onDateRangeSearch,
  onOpenQuotes,
  onCombineQuotes,
  onUncombineQuote,
  isCombining,
  isUncombining,
}: QuotesTableProps) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const combinedRowBg = useColorModeValue('blue.50', 'blue.900');
  const childRowBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const lobIconColor = useColorModeValue('brand.500', 'brand.400');

  const { colorMode } = useColorMode();
  const [brand500] = useToken('colors', ['brand.500']);
  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure();

  const [showSearchFilters, setShowSearchFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [periodDates, setPeriodDates] = useState({
    startDate: parseDateString(dateRange.from),
    endDate: parseDateString(dateRange.to),
    key: 'selection',
  });

  useEffect(() => {
    setPeriodDates({
      startDate: parseDateString(dateRange.from),
      endDate: parseDateString(dateRange.to),
      key: 'selection',
    });
  }, [dateRange.from, dateRange.to]);

  const processedRows = useMemo((): ProcessedRow[] => {
    if (!quotes) return [];

    const processedGroupIds = new Set<number>();
    const rows: ProcessedRow[] = [];

    for (const quote of quotes) {
      if (quote.group_id) {
        if (processedGroupIds.has(quote.group_id)) {
          continue;
        }
        processedGroupIds.add(quote.group_id);

        const otherQuotes = quotes.filter(
          (q) => q.group_id === quote.group_id && q.id !== quote.id,
        );
        rows.push({
          type: 'combined',
          quote,
          groupQuotes: [quote, ...otherQuotes],
        });
      } else {
        rows.push({ type: 'single', quote });
      }
    }

    return rows;
  }, [quotes]);

  const canCombineSelection = useMemo(() => {
    if (selectedIds.length < 2 || !quotes) return false;
    const selectedQuotes = quotes.filter((q) => selectedIds.includes(q.id));
    if (selectedQuotes.length !== selectedIds.length) return false;

    const noneGrouped = selectedQuotes.every((q) => !q.group_id);
    return noneGrouped;
  }, [selectedIds, quotes]);

  const toggleGroupExpanded = useCallback((groupId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const handleCombine = useCallback(() => {
    if (canCombineSelection && onCombineQuotes) {
      onCombineQuotes(selectedIds);
    }
  }, [canCombineSelection, onCombineQuotes, selectedIds]);

  const handleDateRangeApply = useCallback(
    (reset: boolean) => {
      if (reset) {
        const today = new Date();
        const from = new Date(today);
        from.setMonth(from.getMonth() - 1);
        const to = new Date(today);
        to.setMonth(to.getMonth() + 1);

        setPeriodDates({
          startDate: from,
          endDate: to,
          key: 'selection',
        });
        onDateRangeSearch({
          from: formatDateToString(from)!,
          to: formatDateToString(to)!,
        });
      } else if (periodDates.startDate && periodDates.endDate) {
        onDateRangeSearch({
          from: formatDateToString(periodDates.startDate)!,
          to: formatDateToString(periodDates.endDate)!,
        });
      }
      onDatePickerClose();
    },
    [periodDates, onDateRangeSearch, onDatePickerClose],
  );

  const handleSelectOne = useCallback(
    (id: number, checked: boolean) => {
      if (checked) {
        onSelectionChange([...selectedIds, id]);
      } else {
        onSelectionChange(
          selectedIds.filter((selectedId) => selectedId !== id),
        );
      }
    },
    [selectedIds, onSelectionChange],
  );

  const renderRowMenu = (leadingItems?: ReactNode) => (
    <Menu isLazy placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label="Quote actions"
        icon={<Icon as={MdMoreVert} />}
        size="sm"
        variant="ghost"
        onClick={(e) => e.stopPropagation()}
      />
      <MenuList minW="200px">
        {leadingItems}
        {leadingItems && <MenuDivider />}
        {ROW_ACTIONS.map((action) => (
          <MenuItem
            key={action.key}
            icon={<Icon as={action.icon} boxSize={4} />}
            isDisabled
          >
            {action.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );

  const renderCombinedRow = (row: ProcessedRow) => {
    const { quote, groupQuotes } = row;
    const groupId = quote.group_id!;
    const isExpanded = expandedGroups.has(groupId);

    const allQuotes = groupQuotes || [quote];
    const rfqRefs = allQuotes.map((q) => q.acturis_rfq_ref || '-').join(', ');
    const carriers = [
      ...new Set(allQuotes.map((q) => q.carrier_name).filter(Boolean)),
    ];
    const carrierDisplay = carriers.length > 0 ? carriers.join(', ') : '-';

    const totalPremium = allQuotes.reduce((sum, q) => {
      const premium = q.premium ? parseFloat(q.premium) : 0;
      return sum + premium;
    }, 0);

    const handleChildQuoteClick = (q: APIMarketplaceQuoteData) => {
      if (onGroupQuoteClick) {
        onGroupQuoteClick(allQuotes, q);
      } else {
        onQuoteClick(q);
      }
    };

    return (
      <React.Fragment key={`group-${groupId}`}>
        <Tr
          bg={combinedRowBg}
          cursor="pointer"
          _hover={{ bg: 'blue.100' }}
          onClick={() => toggleGroupExpanded(groupId)}
        >
          <Td pl={{ base: 2, md: 4 }} pr={0} minW="40px">
            <Flex align="center" gap={1}>
              <IconButton
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                size="xs"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroupExpanded(groupId);
                }}
              />
            </Flex>
          </Td>
          <Td>
            <Flex align="center" gap={2}>
              <Icon as={LinkIcon} color="blue.500" boxSize={3} />
              <Text
                color={textColor}
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                textTransform="uppercase"
              >
                Combined
              </Text>
            </Flex>
          </Td>
          <Td minW="60px">
            <Text
              color={textColor}
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="700"
              noOfLines={1}
            >
              {rfqRefs}
            </Text>
          </Td>
          <Td display={{ base: 'none', md: 'table-cell' }} minW="100px">
            <Text
              color={textColor}
              fontSize="sm"
              fontWeight="700"
              noOfLines={1}
              title={carrierDisplay}
            >
              {carrierDisplay}
            </Text>
          </Td>
          <Td minW="80px">
            <Text
              color={textColor}
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="700"
              whiteSpace="nowrap"
            >
              {totalPremium > 0 ? formatCurrency(String(totalPremium)) : '-'}
            </Text>
          </Td>
          <Td display={{ base: 'none', lg: 'table-cell' }}>
            <Text color={textColor} fontSize="sm" fontWeight="700">
              -
            </Text>
          </Td>
          <Td display={{ base: 'none', sm: 'table-cell' }} minW="90px">
            <Text
              color={textColor}
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="700"
              whiteSpace="nowrap"
            >
              {formatDate(quote.effective_date)}
            </Text>
          </Td>
          <Td onClick={(e) => e.stopPropagation()} minW="40px">
            {renderRowMenu(
              onUncombineQuote && (
                <MenuItem
                  icon={<Icon as={MdLinkOff} boxSize={4} />}
                  onClick={() => onUncombineQuote(quote.id)}
                  isDisabled={isUncombining}
                >
                  Uncombine Quotes
                </MenuItem>
              ),
            )}
          </Td>
        </Tr>

        {isExpanded &&
          allQuotes.map((q) => (
            <Tr
              key={`child-${q.id}`}
              bg={childRowBg}
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              onClick={() => handleChildQuoteClick(q)}
            >
              <Td pl={{ base: 4, md: 8 }} pr={0}>
                <Text color="gray.400" fontSize="xs">
                  └
                </Text>
              </Td>
              <Td>
                <Flex align="center" gap={2}>
                  <Icon
                    as={formatPolicyType(q.line_of_business).icon}
                    color={lobIconColor}
                    boxSize={4}
                  />
                  <Text
                    color={textColor}
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight="700"
                    textTransform="uppercase"
                  >
                    {formatPolicyType(q.line_of_business).policyType}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Text
                  color={textColor}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                >
                  {q.acturis_rfq_ref || '-'}
                </Text>
              </Td>
              <Td display={{ base: 'none', md: 'table-cell' }}>
                <Text
                  color={textColor}
                  fontSize="sm"
                  fontWeight="700"
                  noOfLines={1}
                >
                  {q.carrier_name || '-'}
                </Text>
              </Td>
              <Td>
                <Text
                  color={textColor}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                  whiteSpace="nowrap"
                >
                  {formatCurrency(q.premium)}
                </Text>
              </Td>
              <Td display={{ base: 'none', lg: 'table-cell' }}>
                <Text color={textColor} fontSize="sm" fontWeight="700">
                  {formatDate(q.quoted_at)}
                </Text>
              </Td>
              <Td display={{ base: 'none', sm: 'table-cell' }}>
                <Text
                  color={textColor}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                  whiteSpace="nowrap"
                >
                  {formatDate(q.effective_date)}
                </Text>
              </Td>
              <Td onClick={(e) => e.stopPropagation()} minW="40px">
                {renderRowMenu()}
              </Td>
            </Tr>
          ))}
      </React.Fragment>
    );
  };

  const getRowBgColor = (quote: QuoteRowData): string | undefined => {
    if (selectedIds.includes(quote.id)) return 'blue.50';
    return undefined;
  };

  const renderSingleRow = (row: ProcessedRow) => {
    const { quote } = row;

    return (
      <Tr
        key={quote.id}
        cursor="pointer"
        _hover={{ bg: 'gray.100' }}
        bg={getRowBgColor(quote)}
      >
        <Td pl={{ base: 2, md: 4 }} pr={0} onClick={(e) => e.stopPropagation()}>
          <Checkbox
            isChecked={selectedIds.includes(quote.id)}
            onChange={(e) => handleSelectOne(quote.id, e.target.checked)}
            colorScheme="blue"
            borderColor="gray.400"
          />
        </Td>
        <Td onClick={() => onQuoteClick(quote)}>
          <Flex align="center" gap={2}>
            <Icon
              as={formatPolicyType(quote.line_of_business).icon}
              color={lobIconColor}
              boxSize={4}
            />
            <Text
              color={textColor}
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="700"
              textTransform="uppercase"
              whiteSpace="nowrap"
            >
              {formatPolicyType(quote.line_of_business).policyType}
            </Text>
          </Flex>
        </Td>
        <Td onClick={() => onQuoteClick(quote)}>
          <Text
            color={textColor}
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="700"
          >
            {quote.acturis_rfq_ref || '-'}
          </Text>
        </Td>
        <Td
          display={{ base: 'none', md: 'table-cell' }}
          onClick={() => onQuoteClick(quote)}
        >
          <Text color={textColor} fontSize="sm" fontWeight="700" noOfLines={1}>
            {quote.carrier_name ||
              (isRfqOnlyLob(quote.line_of_business) ? 'Unselected' : '-')}
          </Text>
        </Td>
        <Td onClick={() => onQuoteClick(quote)}>
          <Text
            color={textColor}
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="700"
            whiteSpace="nowrap"
          >
            {isRfqOnlyLob(quote.line_of_business) && !quote.premium
              ? 'Pending'
              : formatCurrency(quote.premium)}
          </Text>
        </Td>
        <Td
          display={{ base: 'none', lg: 'table-cell' }}
          onClick={() => onQuoteClick(quote)}
        >
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {formatDate(quote.quoted_at)}
          </Text>
        </Td>
        <Td
          display={{ base: 'none', sm: 'table-cell' }}
          onClick={() => onQuoteClick(quote)}
        >
          <Text
            color={textColor}
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="700"
            whiteSpace="nowrap"
          >
            {formatDate(quote.effective_date)}
          </Text>
        </Td>
        <Td onClick={(e) => e.stopPropagation()} minW="40px">
          {renderRowMenu()}
        </Td>
      </Tr>
    );
  };

  return (
    <Card flexDirection="column" flex="1.5" px="0px" pt="16px">
      <Flex px="20px" pb="16px" justify="space-between" align="center">
        <Text color={textColor} fontSize="lg" fontWeight="700">
          Quotes
        </Text>
        <Flex align="center" gap={2}>
          {selectedIds.length > 0 && (
            <Button
              leftIcon={<ExternalLinkIcon />}
              size="sm"
              variant="outline"
              onClick={onOpenQuotes}
            >
              Open Quotes ({selectedIds.length})
            </Button>
          )}
          {canCombineSelection && onCombineQuotes && (
            <Button
              leftIcon={<LinkIcon />}
              size="sm"
              colorScheme="blue"
              variant="outline"
              onClick={handleCombine}
              isLoading={isCombining}
              loadingText="Combining..."
            >
              Combine Quotes
            </Button>
          )}
          <Tooltip
            label={showSearchFilters ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchFilters ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              onClick={() => setShowSearchFilters(!showSearchFilters)}
              size="md"
              variant={showSearchFilters ? 'solid' : 'ghost'}
              colorScheme={showSearchFilters ? 'yellow' : 'gray'}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Collapse in={showSearchFilters} animateOpacity>
        <Box
          px="20px"
          pt="16px"
          pb="16px"
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <Flex gap={3} align="center" flexWrap="wrap">
            <Box minW="180px">
              <Select
                variant="main"
                value={filters.lineOfBusiness}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    lineOfBusiness: e.target.value,
                  })
                }
                bg={inputBg}
                size="md"
                placeholder="All Policy Types"
              >
                {LINES_OF_BUSINESS.map((lob) => (
                  <option key={lob.value} value={lob.value}>
                    {lob.label}
                  </option>
                ))}
              </Select>
            </Box>
            <Box>
              <Popover
                placement="bottom"
                closeOnBlur={true}
                isLazy
                isOpen={isDatePickerOpen}
                onOpen={onDatePickerOpen}
                onClose={onDatePickerClose}
              >
                <PopoverTrigger>
                  <Button
                    leftIcon={<CalendarIcon />}
                    variant={
                      periodDates.startDate || periodDates.endDate
                        ? 'solid'
                        : 'outline'
                    }
                    colorScheme={
                      periodDates.startDate || periodDates.endDate
                        ? 'yellow'
                        : 'gray'
                    }
                    size="md"
                  >
                    {periodDates.startDate && periodDates.endDate
                      ? `${periodDates.startDate.toLocaleDateString()} - ${periodDates.endDate.toLocaleDateString()}`
                      : 'Effective Date Range'}
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent
                    w="auto"
                    maxW="95vw"
                    mt="4"
                    zIndex={1400}
                    overflowX="auto"
                  >
                    <PopoverBody>
                      <DateRangePicker
                        onChange={(item: {
                          selection: {
                            startDate: Date;
                            endDate: Date;
                            key: string;
                          };
                        }) => setPeriodDates(item.selection)}
                        ranges={[periodDates]}
                        firstDayOfWeek={1}
                        direction="horizontal"
                        months={1}
                        className={`date-range-picker ${colorMode}`}
                        rangeColors={[brand500]}
                      />
                      <Flex gap={2} mt="3">
                        <Button
                          onClick={() => handleDateRangeApply(false)}
                          variant="brand"
                          size="sm"
                        >
                          Apply Date Filter
                        </Button>
                        <Button
                          onClick={() => handleDateRangeApply(true)}
                          variant="outline"
                          size="sm"
                        >
                          Reset Dates
                        </Button>
                      </Flex>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </Box>
          </Flex>
        </Box>
      </Collapse>

      {error && (
        <Box p="20px">
          <Alert status="error">
            <AlertIcon />
            Failed to load quotes
          </Alert>
        </Box>
      )}

      {!error && (
        <>
          <Box overflowX="auto">
            <Table variant="simple" color="gray.500" minW="600px">
              <Thead>
                <Tr>
                  <Th
                    w="40px"
                    px={2}
                    borderColor={borderColor}
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                  />
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Policy Type
                  </Th>
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                    fontSize={{ base: 'xs', md: 'sm' }}
                    whiteSpace="nowrap"
                  >
                    Quote #
                  </Th>
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                    display={{ base: 'none', md: 'table-cell' }}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Carrier
                  </Th>
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Premium
                  </Th>
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                    display={{ base: 'none', lg: 'table-cell' }}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Quoted
                  </Th>
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                    display={{ base: 'none', sm: 'table-cell' }}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    Effective
                  </Th>
                  <Th
                    w="50px"
                    borderColor={borderColor}
                    borderTop="1px solid"
                    borderTopColor={borderColor}
                  />
                </Tr>
              </Thead>
              <Tbody>
                {isFetchingRfqs && processedRows.length === 0 ? (
                  <Tr key="skeleton-row">
                    <Td pl={{ base: 2, md: 4 }} pr={0} py={4}>
                      <Skeleton height="16px" width="16px" />
                    </Td>
                    <Td py={4}>
                      <Box minH="32px">
                        <Skeleton
                          height="10px"
                          width="60px"
                          mb={1}
                          display={{ base: 'none', sm: 'block' }}
                        />
                        <Skeleton height="14px" width="40px" />
                      </Box>
                    </Td>
                    <Td py={4}>
                      <Skeleton height="14px" width="80px" />
                    </Td>
                    <Td display={{ base: 'none', md: 'table-cell' }} py={4}>
                      <Skeleton height="14px" width="100px" />
                    </Td>
                    <Td py={4}>
                      <Skeleton height="14px" width="70px" />
                    </Td>
                    <Td display={{ base: 'none', lg: 'table-cell' }} py={4}>
                      <Skeleton height="14px" width="80px" />
                    </Td>
                    <Td display={{ base: 'none', sm: 'table-cell' }} py={4}>
                      <Skeleton height="14px" width="80px" />
                    </Td>
                    <Td py={4} minW="40px">
                      <Skeleton height="24px" width="24px" borderRadius="md" />
                    </Td>
                  </Tr>
                ) : processedRows.length > 0 ? (
                  processedRows.map((row) =>
                    row.type === 'combined'
                      ? renderCombinedRow(row)
                      : renderSingleRow(row),
                  )
                ) : (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={8}>
                      <Text color="gray.500">No quotes found</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Card>
  );
};

export default QuotesTable;
